/**
 * The hangar showcase (Phase 6): one mech on the stage under the shared render host, an orbit
 * camera the pilot can drag, depth of field focused on the mech, idle emotes, and a loadout
 * applied to the view without recompiling anything. Rendering only: every decision about what
 * the account may equip is the API's.
 */
import { DEFAULT_ARENA_ENVIRONMENT } from '@steelwake/shared/arena';
import type { Loadout } from '@steelwake/shared/loadout';
import { uniform } from 'three/tsl';
import { PerspectiveCamera, Scene, Vector3 } from 'three/webgpu';

import type { EffectConfig } from '../../settings/presets.js';
import type { Settings } from '../../settings/schema.js';
import { loadEnvironment } from '../assets/environment.js';
import { acquireLoaders, type Loaders } from '../assets/loaders.js';
import { assetUrl } from '../assets/paths.js';
import type { BackendName } from '../backend.js';
import {
  RenderHost,
  type FrameTiming,
  type RenderSnapshot,
  type RenderStatsSource,
} from '../render/host.js';
import type { UrlOptions } from '../urlParams.js';
import { lookFor, type MechLook } from '../view/look.js';
import { MechAssetCache, type MechAsset } from '../view/mechAsset.js';
import { MechView } from '../view/mechView.js';
import {
  initialOrbit,
  NO_ORBIT_INPUT,
  orbitPosition,
  orbitStep,
  reframeOrbit,
  type OrbitInput,
  type OrbitState,
} from './orbit.js';
import { Stage, stageQualityOf } from './stage.js';

/** The showcase keeps the industrial hall's light whichever arena the pilot last fought in. */
const HDRI_PATH = DEFAULT_ARENA_ENVIRONMENT.hdri;
const READY_FRAMES = 4;
/** The mech faces the camera's home angle. */
const MODEL_YAW = Math.PI;
const EMOTES = ['Hello', 'Dance'] as const;

export type HangarPhase = 'probing' | 'initialising' | 'loading' | 'ready' | 'error';

export interface HangarStatus {
  phase: HangarPhase;
  message?: string;
}

export interface HangarOptions {
  canvas: HTMLCanvasElement;
  container: HTMLElement;
  signal: AbortSignal;
  settings: Settings;
  url: UrlOptions;
  /** The mech to show first. */
  mech: string;
  /** The loadout to show first; null for the mech's stock look. */
  loadout: Loadout | null;
  onStatus(status: HangarStatus): void;
}

export interface HangarSnapshot {
  mech: string;
  loadout: Loadout | null;
  /** Null until the first mech is on the stage. */
  look: MechLook | null;
  /** Cosmetic parts currently shown. */
  parts: number;
  boundsHeight: number;
  orbit: { yaw: number; pitch: number; radius: number };
  reflections: boolean;
  animation: { clip: string | null; time: number; weight: number; running: boolean } | null;
}

export class HangarHost implements RenderStatsSource {
  ready = false;
  error: string | null = null;

  private readonly scene = new Scene();
  private readonly camera = new PerspectiveCamera(38, 16 / 9, 0.1, 300);
  private readonly host: RenderHost;
  private readonly focus = uniform(12);
  private readonly target = new Vector3();
  private loaders: Loaders | null = null;
  private assets: MechAssetCache | null = null;
  private stage: Stage | null = null;
  private disposeEnvironment: (() => void) | null = null;
  private view: MechView | null = null;
  private asset: MechAsset | null = null;
  /** The previous view, removed once the new one has been drawn. */
  private retiring: MechView | null = null;
  private mech: string;
  private loadout: Loadout | null;
  private selection = 0;
  private orbit: OrbitState = initialOrbit(6);
  private readonly input: OrbitInput = { ...NO_ORBIT_INPUT };
  private dragging = false;
  private emoteIn = 7;
  private emoteIndex = 0;
  private disposed = false;
  private readonly listeners: (() => void)[] = [];

  constructor(private readonly opts: HangarOptions) {
    this.mech = opts.mech;
    this.loadout = opts.loadout;
    this.host = new RenderHost({
      canvas: opts.canvas,
      container: opts.container,
      signal: opts.signal,
      settings: opts.settings,
      url: opts.url,
      scene: this.scene,
      camera: this.camera,
      dofFocus: this.focus,
      // No SSGI on the stage: with this scene the SSGI pass never finishes its first WebGPU node
      // build (three r186); the stage has AO, the environment and depth of field instead.
      mask: (config) => ({ ...config, ssgi: null }),
      onResize: (width, height) => {
        this.camera.aspect = width / height;
        this.camera.updateProjectionMatrix();
      },
      onFrame: (count) => {
        if (this.retiring) {
          this.retiring.dispose();
          this.retiring = null;
        }
        if (!this.ready && count >= READY_FRAMES) {
          this.ready = true;
          this.setStatus({ phase: 'ready' });
        }
      },
    });
  }

  get backend(): BackendName | null {
    return this.host.backend;
  }

  get gpuTiming(): boolean {
    return this.host.gpuTiming;
  }

  get frameCount(): number {
    return this.host.frameCount;
  }

  async start(): Promise<void> {
    const { signal, canvas } = this.opts;
    this.setStatus({ phase: 'probing' });
    const handle = await this.host.init((backend) =>
      this.setStatus({ phase: 'initialising', message: backend }),
    );
    signal.throwIfAborted();
    this.setStatus({ phase: 'loading' });
    const loaders = acquireLoaders(handle.renderer);
    this.loaders = loaders;
    const anisotropy = Math.min(this.host.effects.anisotropy, handle.maxAnisotropy);
    const assets = new MechAssetCache(loaders, anisotropy);
    this.assets = assets;
    this.stage = new Stage(stageQualityOf(this.host.effects, this.opts.url.e2e));
    this.scene.add(this.stage.root);
    const [disposeEnvironment, asset] = await Promise.all([
      loadEnvironment(this.scene, assetUrl(HDRI_PATH), signal, { background: false }),
      assets.load(this.mech, signal),
    ]);
    signal.throwIfAborted();
    this.disposeEnvironment = disposeEnvironment;
    this.show(asset);
    this.bindPointer(canvas);
    this.host.begin((timing) => this.frame(timing));
  }

  /** Swaps the mech on the stage; the old one stays until the new one has been drawn. */
  async selectMech(mechId: string, loadout: Loadout | null): Promise<void> {
    const id = ++this.selection;
    this.mech = mechId;
    this.loadout = loadout;
    // Before the assets exist (start() still initialising), start() picks up the new choice.
    const assets = this.assets;
    if (!assets) return;
    const asset = await assets.load(mechId, this.opts.signal);
    if (this.disposed || id !== this.selection) return;
    this.show(asset);
  }

  /** Repaints and re-equips the shown mech; nothing is rebuilt. */
  setLoadout(loadout: Loadout | null): void {
    this.loadout = loadout;
    if (this.view && this.asset) this.view.setLook(lookFor(this.asset.sidecar, loadout));
  }

  applySettings(next: Settings): void {
    const change = this.host.applySettings(next);
    if (!this.host.renderer) return;
    this.stage?.apply(stageQualityOf(change.config, this.opts.url.e2e));
    if (change.anisotropyChanged) {
      this.assets?.setAnisotropy(Math.min(change.config.anisotropy, this.host.maxAnisotropy));
    }
  }

  snapshot(): RenderSnapshot {
    return this.host.snapshot();
  }

  resetStats(): void {
    this.host.resetStats();
  }

  quality(): EffectConfig {
    return this.host.effects;
  }

  hangar(): HangarSnapshot {
    const look = this.view?.look() ?? null;
    return {
      mech: this.mech,
      loadout: this.loadout,
      look,
      parts: look?.parts.length ?? 0,
      boundsHeight: this.asset?.sidecar.boundsHeight ?? 0,
      orbit: { yaw: this.orbit.yaw, pitch: this.orbit.pitch, radius: this.orbit.radius },
      reflections: this.stage?.current().reflections ?? false,
      animation: this.view?.animation() ?? null,
    };
  }

  fail(message: string): void {
    this.error = message;
    this.setStatus({ phase: 'error', message });
  }

  stop(): void {
    this.host.stop();
  }

  async dispose(): Promise<void> {
    this.disposed = true;
    this.host.stop();
    for (const off of this.listeners) off();
    this.listeners.length = 0;
    this.view?.dispose();
    this.retiring?.dispose();
    this.view = null;
    this.retiring = null;
    this.assets?.dispose();
    this.stage?.dispose();
    this.disposeEnvironment?.();
    this.loaders?.release();
    await this.host.dispose();
  }

  private show(asset: MechAsset): void {
    const view = new MechView(asset, lookFor(asset.sidecar, this.loadout));
    view.setPose(0, 0, 0, MODEL_YAW);
    this.scene.add(view.root);
    if (this.retiring) this.retiring.dispose();
    this.retiring = this.view;
    this.view = view;
    this.asset = asset;
    this.orbit = reframeOrbit(this.orbit, asset.sidecar.boundsHeight);
    this.emoteIn = this.opts.url.e2e ? 9 : 6 + Math.random() * 6;
  }

  private frame(timing: FrameTiming): void {
    const view = this.view;
    if (this.disposed || !view) return;
    const input: OrbitInput = { ...this.input, dragging: this.dragging };
    this.input.dragX = 0;
    this.input.dragY = 0;
    this.input.wheel = 0;
    this.orbit = orbitStep(this.orbit, input, timing.dt);
    const p = orbitPosition(this.orbit);
    this.camera.position.set(p.x, p.y, p.z);
    this.target.set(this.orbit.target.x, this.orbit.target.y, this.orbit.target.z);
    this.camera.lookAt(this.target);
    this.focus.value = this.camera.position.distanceTo(this.target);

    this.emoteIn -= timing.dt;
    if (this.emoteIn <= 0) {
      const name = EMOTES[this.emoteIndex % EMOTES.length] ?? 'Hello';
      this.emoteIndex++;
      view.playEmote(name);
      this.emoteIn = this.opts.url.e2e ? 9 : 8 + Math.random() * 8;
    }
    view.update(timing.dt);
    view.updateLod(this.camera.position);
  }

  private bindPointer(canvas: HTMLCanvasElement): void {
    let lastX = 0;
    let lastY = 0;
    const down = (e: PointerEvent) => {
      if (e.button !== 0) return;
      this.dragging = true;
      lastX = e.clientX;
      lastY = e.clientY;
      canvas.setPointerCapture(e.pointerId);
    };
    const move = (e: PointerEvent) => {
      if (!this.dragging) return;
      this.input.dragX += e.clientX - lastX;
      this.input.dragY += e.clientY - lastY;
      lastX = e.clientX;
      lastY = e.clientY;
    };
    const up = (e: PointerEvent) => {
      if (!this.dragging) return;
      this.dragging = false;
      if (canvas.hasPointerCapture(e.pointerId)) canvas.releasePointerCapture(e.pointerId);
    };
    const wheel = (e: WheelEvent) => {
      e.preventDefault();
      this.input.wheel += e.deltaY;
    };
    canvas.addEventListener('pointerdown', down);
    canvas.addEventListener('pointermove', move);
    canvas.addEventListener('pointerup', up);
    canvas.addEventListener('pointercancel', up);
    canvas.addEventListener('wheel', wheel, { passive: false });
    canvas.style.touchAction = 'none';
    this.listeners.push(() => {
      canvas.removeEventListener('pointerdown', down);
      canvas.removeEventListener('pointermove', move);
      canvas.removeEventListener('pointerup', up);
      canvas.removeEventListener('pointercancel', up);
      canvas.removeEventListener('wheel', wheel);
    });
  }

  private setStatus(status: HangarStatus): void {
    if (!this.disposed) this.opts.onStatus(status);
  }
}
